import AppContext from 'App/AppContext';
import { IPPivot, IPSpinner } from 'baseComponents';
import { IPPivotItem } from 'baseComponents/Pivot/IPPivotItem';
import { IPSpinnerSize } from 'baseComponents/Spinner/IPSpinner';
import { IPStack } from 'baseComponents/Stack/IPStack';
import ActivityTrail from 'components/Activity/ActivityTrail';
import AuditTrail from 'components/Audit/AuditTrail';
import { ResourcePanelAuditTasks } from 'components/Audit/ResourcePanelAuditTasks';
import { useAuditTaskResourceTab } from 'components/Audit/useAuditTaskResourceTab';
import { isEntityEvidenceAuditAvailable } from 'components/Copilot/copilotPanelAvailability';
import { useCopilotEntityReload } from 'components/Copilot/useCopilotEntityReload';
import { ProcessingActivityLinkList } from 'components/Links/LinkLists/ProcessingActivityLinkList';
import { LinkPreview } from 'components/Links/Preview/LinkPreview';
import ResourcePanel from 'components/ResourcePanel/ResourcePanel';
import SceneBar from 'components/SceneBar/SceneBar';
import { getCurrentPivotKeyFromURL, IScenePivotItemProps } from 'components/SceneBar/ScenePivot';
import { globalNotFoundURL } from 'globalConstants';
import {
    globalStackItemStylesPaddingScene,
    globalStackStylesHeight100,
    globalStackTokensGapMedium,
} from 'globalStyles';
import { CopilotPanelContext } from 'models/copilot/copilotBridge';
import { useCopilotContext } from 'models/copilot/copilotContext';
import { CopilotGenericContent } from 'models/copilot/copilotGeneric';
import { ProcessingActivityLinkType } from 'models/dto/processingActivity/processingActivityLinkDTO';
import { EntityTypes } from 'models/entity';
import ProcessingActivity from 'models/processingActivity';
import ResourceLink from 'models/resourceLink';
import Task from 'models/tasks/task';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Redirect, Route, RouteComponentProps, Switch, useHistory } from 'react-router-dom';
import { apiGetLinksForProcessingActivity } from 'services/Api/linkService';
import { apiGetProcessingActivity } from 'services/Api/processingActivityService';
import { apiGetSingleTask } from 'services/Api/taskService';
import { apiRequest } from 'services/Auth/authConfig';
import { parsePositiveIntegerRouteParam } from 'utils/url';
import { ProcessingActivityContext } from './ProcessingActivityContext';
import { TabDashboard } from './TabDashboard';
import { TabDetails } from './TabDetails';
import TabTasks from './TabTasks';

interface IProcessingActivityDetailsProps extends RouteComponentProps {}
interface IProcessingActivityDetailsProps {}

const ProcessingActivityDetails = (props: IProcessingActivityDetailsProps) => {
  const { t } = useTranslation(['translation', 'processingActivity', 'audit']);
  const appContext = useContext(AppContext);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const _sceneBaseURL = '/processingactivity';
  const [selectedProcessingActivity, setSelectedProcessingActivity] = useState<ProcessingActivity | undefined>(
    undefined,
  );
  const [dbSelectedProcessingActivity, setDbSelectedProcessingActivity] = useState<ProcessingActivity | undefined>(
    undefined,
  );
  const [pinnedLinks, setPinnedLinks] = useState<ResourceLink[]>([]);
  const [pinnedLink, setPinnedLink] = useState<ResourceLink | undefined>(undefined);
  const [showLinkInModal, setShowLinkInModal] = useState<ResourceLink | undefined>(undefined);
  const [dpiaAnalysisTask, setDpiaAnalysisTask] = useState<Task | undefined>(undefined);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const history = useHistory();
  const {
    selectedPivot,
    setSelectedPivot,
    auditTaskCount,
    initialTaskId,
    initialInternalAuditId,
    onAvailabilityChange,
  } = useAuditTaskResourceTab(
    EntityTypes.ProcessingActivity,
    selectedProcessingActivity?.processingActivityId,
    'context',
  );

  const evidenceAuditAvailable = isEntityEvidenceAuditAvailable(appContext, selectedProcessingActivity?.authSchemaId);
  const copilotContent = useMemo(() => {
    const content = new CopilotGenericContent();
    content.entityType = EntityTypes.ProcessingActivity;
    content.entityId = selectedProcessingActivity?.processingActivityId;
    content.canAuditEvidence = evidenceAuditAvailable;

    return content;
  }, [selectedProcessingActivity?.processingActivityId, evidenceAuditAvailable]);
  useCopilotContext(appContext.copilotBridge, CopilotPanelContext.Generic, copilotContent);

  // A chat answer or an approval about this processing activity landed: re-read it, with its links as
  // loadData does, while the scene is clean.
  useCopilotEntityReload(
    EntityTypes.ProcessingActivity,
    selectedProcessingActivity?.processingActivityId,
    hasUnsavedChanges,
    async (processingActivityId: number) => {
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const processingActivity = await apiGetProcessingActivity(
        processingActivityId,
        accessToken,
        appContext.globalDataCache,
      );
      if (processingActivity) await loadLinks(processingActivity);

      return processingActivity;
    },
    (processingActivity: ProcessingActivity) => {
      setSelectedProcessingActivity(processingActivity.clone());
      setDbSelectedProcessingActivity(processingActivity);
    },
  );

  const pivotItems: IScenePivotItemProps[] = [
    { key: 'details', text: t('processingActivity:Pivot.Details'), url: `/details` },
    { key: 'dashboard', text: t('processingActivity:Pivot.Dashboard'), url: `/dashboard` },
    { key: 'tasks', text: t('processingActivity:Pivot.Tasks'), url: `/tasks` },
  ];

  const onBadgeClick = (badgeText?: string) => {
    if (!badgeText) return;

    //check pinned links
    let badgeLinkExact = pinnedLinks.find((l) => l.linkName.toLowerCase() === badgeText.toLowerCase());
    if (badgeLinkExact) {
      setPinnedLink(badgeLinkExact);
    } else {
      let badgeLinkSimilar = pinnedLinks.find((l) => l.linkName.toLowerCase().includes(badgeText.toLowerCase()));
      if (badgeLinkSimilar) {
        setPinnedLink(badgeLinkSimilar);
      } else {
        //check other links
        badgeLinkExact = selectedProcessingActivity?.resourceLinks?.find(
          (l) => l.linkName.toLowerCase() === badgeText.toLowerCase(),
        );
        if (badgeLinkExact) {
          setShowLinkInModal(badgeLinkExact);
        } else {
          badgeLinkSimilar = selectedProcessingActivity?.resourceLinks?.find((l) =>
            l.linkName.toLowerCase().includes(badgeText.toLowerCase()),
          );
          if (badgeLinkSimilar) {
            setShowLinkInModal(badgeLinkSimilar);
          } else {
            window.alert(t('translation:General.Notifications.BadgeNotFound', { badgeText: badgeText }));
          }
        }
      }
    }
  };

  useEffect(() => {
    const urlProcessingActivityId = getProcessingActivityFromRoute();
    loadData(urlProcessingActivityId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history.location]);

  const loadData = async (processingActivityId: number) => {
    if (selectedProcessingActivity?.processingActivityId === processingActivityId) return;
    if (processingActivityId < 0 || !processingActivityId) {
      props.history.replace(globalNotFoundURL);

      return;
    }

    try {
      setIsLoading(true);
      appContext.showContentLoader();

      await appContext.globalDataCache.tags.getItems();

      // load processing activity data
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newProcessingActivity = await apiGetProcessingActivity(
        processingActivityId,
        accessToken,
        appContext.globalDataCache,
      );

      if (!newProcessingActivity || !newProcessingActivity.processingActivityId) {
        // if not found, navigate to not-found
        props.history.replace(globalNotFoundURL);

        return;
      }

      if (newProcessingActivity.taskId_DPIA) {
        const dpiaTasks = await apiGetSingleTask(
          newProcessingActivity.taskId_DPIA,
          false,
          accessToken,
          appContext.globalDataCache,
        );
        if (dpiaTasks && dpiaTasks.tasks && dpiaTasks.tasks.length > 0) {
          setDpiaAnalysisTask(dpiaTasks.tasks[0]);
        }
      }

      await loadLinks(newProcessingActivity);

      setSelectedProcessingActivity(newProcessingActivity.clone());
      setDbSelectedProcessingActivity(newProcessingActivity);
      setPinnedLink(undefined);
    } catch (err) {
      appContext.setError(err);
      props.history.replace(globalNotFoundURL);
    } finally {
      appContext.hideContentLoader();
      setIsLoading(false);
    }
  };

  const getProcessingActivityFromRoute = (): number => {
    try {
      const params: Record<string, string | undefined> = props.match.params;
      const urlProcessingActivityId = parsePositiveIntegerRouteParam(params.urlProcessingActivityParam);
      if (urlProcessingActivityId === undefined) {
        props.history.replace(globalNotFoundURL);

        return NaN;
      }

      return urlProcessingActivityId;
    } catch (err) {
      appContext.setError(err);

      return NaN;
    }
  };

  const loadLinks = async (processingActivity: ProcessingActivity) => {
    const accessToken = await appContext.getAccessToken(apiRequest.scopes);
    const newLinks = await apiGetLinksForProcessingActivity(
      processingActivity.processingActivityId,
      accessToken,
      appContext.globalDataCache,
    );

    processingActivity.resourceLinks = newLinks;
    // Pinned links, SCC-links and Joint Controller Agreements are all pinned.
    setPinnedLinks(newLinks.filter((l) => l.linkTypeId !== ProcessingActivityLinkType.Default));
    setPinnedLink(undefined);
  };

  const updateProcessingActivity = (processingActivity: ProcessingActivity | undefined) => {
    setSelectedProcessingActivity(processingActivity);
  };

  const onPinLink = (link: ResourceLink, pinned: boolean) => {
    let newPinnedLinks = [...pinnedLinks];
    if (pinned) {
      newPinnedLinks = newPinnedLinks.filter((l) => l.linkId !== link.linkId);
      newPinnedLinks.push(link);
      if (newPinnedLinks.length === 1) {
        if (link.linkTypeId === ProcessingActivityLinkType.Pinned) setPinnedLink(link);
        setPinnedLinks(newPinnedLinks);
      } else {
        setPinnedLinks(newPinnedLinks);
      }
    } else {
      newPinnedLinks = newPinnedLinks.filter((l) => l.linkId !== link.linkId);
      if (pinnedLink?.linkId === link.linkId) {
        setPinnedLink(undefined);
        setPinnedLinks(newPinnedLinks);
      } else {
        setPinnedLinks(newPinnedLinks);
      }
    }
  };

  const onRemoveLink = (link: ResourceLink) => {
    if (!selectedProcessingActivity) return;
    const newProcessingActivity = selectedProcessingActivity.clone();
    newProcessingActivity.resourceLinks = newProcessingActivity.resourceLinks?.filter((l) => l.linkId !== link.linkId);
    setSelectedProcessingActivity(newProcessingActivity);
  };

  const onAddLink = (links: ResourceLink[]) => {
    if (!selectedProcessingActivity) return;
    const newProcessingActivity = selectedProcessingActivity.clone();
    newProcessingActivity.resourceLinks.push(...links);
    setSelectedProcessingActivity(newProcessingActivity);
  };

  const getResourceContent = () => {
    return (
      <IPStack verticalFill tokens={globalStackTokensGapMedium}>
        <IPStack.Item styles={{ root: { width: '90%' } }}>
          <IPPivot
            selectedKey={selectedPivot}
            onLinkClick={handlePivotClick}
            headersOnly={true}
            overflowBehavior="menu"
          >
            {auditTaskCount > 0 && (
              <IPPivotItem
                key="auditTasks"
                headerText={
                  auditTaskCount > 1
                    ? t('audit:Execute.Task.RelatedTasks.TabWithCount', { count: auditTaskCount })
                    : t('audit:Execute.Task.RelatedTasks.Tab')
                }
                itemKey="auditTasks"
              />
            )}
            <IPPivotItem key="context" headerText={t('processingActivity:Resources.Context')} itemKey="context" />
            <IPPivotItem
              key="activities"
              headerText={t('processingActivity:Resources.Activities')}
              itemKey="activities"
            />
            <IPPivotItem key="library" headerText={t('processingActivity:Resources.Library')} itemKey="library" />
            <IPPivotItem key="audit" headerText={t('processingActivity:Resources.ChangeLog')} itemKey="audit" />
          </IPPivot>
        </IPStack.Item>
        <IPStack.Item grow styles={{ root: { minHeight: 0 } }}>
          {selectedProcessingActivity && (
            <ResourcePanelAuditTasks
              entityType={EntityTypes.ProcessingActivity}
              entityId={selectedProcessingActivity.processingActivityId}
              initialTaskId={initialTaskId}
              initialInternalAuditId={initialInternalAuditId}
              isOpen={selectedPivot === 'auditTasks'}
              onAvailabilityChange={onAvailabilityChange}
            />
          )}
          {selectedPivot === 'context' && selectedProcessingActivity && (
            <ProcessingActivityContext processingActivity={selectedProcessingActivity} />
          )}
          {selectedPivot === 'activities' && selectedProcessingActivity && (
            <ActivityTrail
              auditTrailId={selectedProcessingActivity?.auditTrailId || 0}
              commentTrailId={selectedProcessingActivity?.commentTrailId || 0}
              processingActivityId={selectedProcessingActivity?.processingActivityId}
              authSchemaIds={[selectedProcessingActivity?.authSchemaId]}
            />
          )}
          {selectedPivot === 'library' && selectedProcessingActivity && (
            <ProcessingActivityLinkList
              processingActivity={selectedProcessingActivity}
              authSchemaId={selectedProcessingActivity?.authSchemaId}
              onPinLink={onPinLink}
              onRemove={onRemoveLink}
              onAdd={onAddLink}
            />
          )}
          {selectedPivot === 'audit' && <AuditTrail auditTrailId={selectedProcessingActivity?.auditTrailId} />}
        </IPStack.Item>
      </IPStack>
    );
  };

  const handlePivotClick = (item?: IPPivotItem) => {
    if (item) {
      setSelectedPivot(item.props.itemKey!);
    }
  };

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  return (
    <IPStack horizontal styles={globalStackStylesHeight100}>
      <IPStack.Item grow styles={{ root: { overflowX: 'scroll' } }}>
        <IPStack verticalFill tokens={globalStackTokensGapMedium}>
          <IPStack.Item>
            <SceneBar
              title={t('processingActivity:Scenebar.Title')}
              subtitle={t('processingActivity:Scenebar.SubTitle')}
              image="processing-activities.png"
              pivotItems={pivotItems}
              selectedPivotKey={pinnedLink ? undefined : getCurrentPivotKeyFromURL(pivotItems)}
              onSelectPivot={() => {
                setPinnedLink(undefined);
              }}
              pinnedLinks={pinnedLinks}
              onSelectPinnedLink={(link) => {
                if (hasUnsavedChanges) {
                  if (!window.confirm(t('translation:General.Notifications.LeaveDirtyPage'))) {
                    return;
                  }
                }
                setPinnedLink(link);
              }}
              selectedPinnedLink={pinnedLink}
              linkUrl={`/processingactivity/${selectedProcessingActivity?.processingActivityId}`}
              showBackButton={true}
              backButtonUrl="/processingactivities"
            ></SceneBar>
          </IPStack.Item>
          {!pinnedLink && (
            <IPStack.Item grow styles={globalStackItemStylesPaddingScene}>
              <Switch>
                <Route
                  path={`${_sceneBaseURL}/:urlProcessingActivityParam/details`}
                  render={() => (
                    <TabDetails
                      isLoading={isLoading}
                      selectedProcessingActivity={selectedProcessingActivity}
                      dbSelectedProcessingActivity={dbSelectedProcessingActivity}
                      updateProcessingActivity={updateProcessingActivity}
                      updateDbProcessingActivity={setDbSelectedProcessingActivity}
                      showLinkInModal={showLinkInModal}
                      setShowLinkInModal={setShowLinkInModal}
                      onBadgeClick={onBadgeClick}
                      dpiaAnalysisTask={dpiaAnalysisTask}
                      setDpiaAnalysisTask={setDpiaAnalysisTask}
                      onPinLink={onPinLink}
                      setHasUnsavedChanges={setHasUnsavedChanges}
                    />
                  )}
                />
                <Route
                  path={`${_sceneBaseURL}/:urlProcessingActivityParam/dashboard`}
                  render={() => (
                    <TabDashboard selectedProcessingActivity={selectedProcessingActivity} isLoading={isLoading} />
                  )}
                />
                <Route
                  path={`${_sceneBaseURL}/:urlProcessParam/tasks`}
                  render={() => (
                    <TabTasks selectedProcessingActivity={selectedProcessingActivity} isLoading={isLoading} />
                  )}
                />
                <Redirect
                  from={`${_sceneBaseURL}/:urlProcessingActivityParam`}
                  to={`${_sceneBaseURL}/:urlProcessingActivityParam/details${history.location.search}`}
                />
                <Redirect to={globalNotFoundURL} />
              </Switch>
            </IPStack.Item>
          )}
          {pinnedLink && (
            <LinkPreview
              link={pinnedLink}
              enableLinks={true}
              title={`${selectedProcessingActivity?.code} ${selectedProcessingActivity?.name}`}
            ></LinkPreview>
          )}
        </IPStack>
      </IPStack.Item>
      {/* Right side bar */}
      <IPStack.Item>
        <ResourcePanel scrollable={selectedPivot !== 'auditTasks'}>{getResourceContent()}</ResourcePanel>
      </IPStack.Item>
    </IPStack>
  );
};

export default ProcessingActivityDetails;
